import * as React from "react";
import { useWindowManager } from "../WindowManagerContext";
import { BRAND } from "../../risikoanalyseStep/tokens";
import { Z_LAYERS } from "../zLayers";
import { abonniereZurueckziehen, ablagePosition, beendeZurueckziehen, zurueckgezogenesFenster } from "./zurueckziehen";

/**
 * Ablagefläche im Hauptfenster fürs Zurückziehen (siehe `zurueckziehen.ts`).
 * Solange eine Kopfleiste aus einem Browserfenster unterwegs ist, nimmt das
 * ganze Hauptfenster sie an und sagt, was beim Loslassen passiert.
 */
export const ZurueckziehenZiel: React.FC<{
  /** Fenster-Layer im Hauptfenster — dorthin rechnet sich die Ablagestelle. */
  layer: () => HTMLElement | null;
}> = ({ layer }) => {
  const { windows, moveWindow, setWindowBrowserFenster } = useWindowManager();
  const id = React.useSyncExternalStore(abonniereZurueckziehen, zurueckgezogenesFenster);
  const fenster = id ? windows.find((w) => w.id === id && w.browserFenster) : undefined;
  const aktuell = React.useRef({ fenster, layer });
  aktuell.current = { fenster, layer };

  React.useEffect(() => {
    if (!id) return;
    // Im Capture am Fenster: vor allen Ablageflächen der Seite (Dateien in die Akte …).
    const annehmen = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    };
    const ablegen = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      beendeZurueckziehen();
      const { fenster: w, layer: l } = aktuell.current;
      if (!w) return;
      const flaeche = l()?.getBoundingClientRect();
      // Angedockt bleibt es an seinem Rand, maximiert bleibt maximiert.
      if (flaeche && w.state === "normal" && !w.angedockt) {
        moveWindow(w.id, ablagePosition({ x: e.clientX, y: e.clientY }, flaeche, w.size));
      }
      setWindowBrowserFenster(w.id, false);
      window.focus();
    };
    // Während des Ziehens kommen keine Mausbewegungen an. Kommt doch eine,
    // ist das Ziehen vorbei, ohne dass es jemand gemeldet hat (Browserfenster
    // mittendrin zu) — dann darf die Fläche nicht liegen bleiben.
    const vorbei = () => beendeZurueckziehen();
    window.addEventListener("dragenter", annehmen, true);
    window.addEventListener("dragover", annehmen, true);
    window.addEventListener("drop", ablegen, true);
    window.addEventListener("mousemove", vorbei, true);
    return () => {
      window.removeEventListener("dragenter", annehmen, true);
      window.removeEventListener("dragover", annehmen, true);
      window.removeEventListener("drop", ablegen, true);
      window.removeEventListener("mousemove", vorbei, true);
    };
  }, [id, moveWindow, setWindowBrowserFenster]);

  if (!fenster) return null;
  // Deckt alles ab, auch die Mail-Vorschau: Ein `<iframe>` bekäme das Ablegen sonst selbst.
  return (
    <div
      data-testid="zurueckziehen-ziel"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: Z_LAYERS.ziehOverlay,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(77, 32, 81, 0.14)",
        outline: `3px dashed ${BRAND.gold}`,
        outlineOffset: -8,
      }}
    >
      <div
        role="status"
        style={{
          maxWidth: 420,
          padding: "14px 20px",
          borderRadius: 10,
          background: BRAND.purple,
          color: BRAND.white,
          fontFamily: BRAND.fontPrimary,
          fontSize: "1rem",
          lineHeight: 1.4,
          textAlign: "center",
          boxShadow: "0 8px 28px rgba(0, 0, 0, 0.25)",
        }}
      >
        Hier loslassen: „{fenster.title}“ kommt zurück ins KMVS.
      </div>
    </div>
  );
};
